import type { FixtureBox, Room } from "@rooms/roomsClient";
import { useSetRoomCamera } from "@rooms/roomsQueries";
import { Button, FieldError } from "@scribenote/brian-design-system";

import { BoxOverlay } from "./BoxOverlay";
import {
  addFixture,
  MAX_FIXTURES,
  removeFixture,
  sameFixtures,
  savedFixtures,
  type SourceSize,
} from "./fixtureBoxes";

/** Drawn over the shown still; a drag adds a box. */
export const MaskOverlay = ({
  size,
  fixtures,
  onChange,
}: {
  size: SourceSize;
  fixtures: readonly FixtureBox[];
  onChange: (next: readonly FixtureBox[]) => void;
}) => (
  <BoxOverlay
    size={size}
    tone="mask"
    label="Mask editor"
    disabled={fixtures.length >= MAX_FIXTURES}
    boxes={fixtures.map((box, index) => ({
      key: String(index),
      box,
      label: String(index + 1),
    }))}
    onDraw={(box) => onChange(addFixture(fixtures, box))}
  />
);

/** Saves through `PUT /rooms/{room}/camera`, which replaces the whole config, so the transport goes back unchanged. */
export const MaskControls = ({
  room,
  fixtures,
  onChange,
  onDone,
}: {
  room: Room;
  fixtures: readonly FixtureBox[];
  onChange: (next: readonly FixtureBox[]) => void;
  onDone: () => void;
}) => {
  const mutation = useSetRoomCamera();
  const saved = room.cameraFixtures ?? [];
  const unchanged = sameFixtures(fixtures, saved);
  const full = fixtures.length >= MAX_FIXTURES;

  return (
    <div
      data-testid="perception-mask-editor"
      className="flex flex-col gap-3 border-t border-rule px-3 py-3"
    >
      <p className="text-sm text-ink-body">
        Drag on the still to mask an area. Brian greys out every mask before it
        reads a still. Masks apply to every camera in this room.
      </p>
      {fixtures.length > 0 ? (
        <ol className="flex flex-col">
          {fixtures.map((box, index) => (
            <li
              key={index}
              className="flex min-w-0 items-center gap-2 border-b border-rule py-1 last:border-b-0"
            >
              <span className="w-6 shrink-0 font-mono text-xs text-ink-declined">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1 truncate font-mono text-xs text-ink-body">
                {box[0]},{box[1]} → {box[2]},{box[3]} px
              </span>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Remove mask ${index + 1}`}
                onClick={() => onChange(removeFixture(fixtures, index))}
              >
                Remove
              </Button>
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-sm text-annotation">No masks in this room.</p>
      )}
      <p className="font-mono text-fine-print text-annotation">
        {fixtures.length} of {MAX_FIXTURES}
        {full && " · remove one to add another"}
      </p>
      {mutation.isError && (
        <FieldError>Couldn&rsquo;t save the mask. Try again.</FieldError>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={mutation.isPending}
          onClick={onDone}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          busy={mutation.isPending}
          disabled={unchanged}
          onClick={() =>
            mutation.mutate(
              {
                roomUuid: room.id,
                transport: room.cameraTransport,
                fixtures: savedFixtures(fixtures),
              },
              { onSuccess: onDone },
            )
          }
        >
          Save mask
        </Button>
      </div>
    </div>
  );
};
